import { useCallback } from 'react';
import type { Industry } from '@dru/types';
import { EntityCombobox } from '@dru/ui';
import { api } from '../lib/api';

/**
 * Picking an industry from the catalogue.
 *
 * The catalogue is small enough to dump into a `<select>` today, and that is
 * exactly how it stops being maintained: nobody adds an industry to a list
 * they have to scroll. Searched instead, opening on the first five, and the
 * industries themselves come from Clinical masters — adding one there makes
 * it appear here without a deploy.
 */
export function IndustryCombobox({
  value, onChange, label = 'Industry', required, error, info, disabled, includeInactive,
}: {
  value: Industry | null;
  onChange: (next: Industry | null) => void;
  label?: string;
  required?: boolean;
  error?: string | null;
  info?: string;
  disabled?: boolean;
  includeInactive?: boolean;
}) {
  const fetchOptions = useCallback(async (q: string) => {
    const res = await api.get<{ items: Industry[] }>(
      `/industries?q=${encodeURIComponent(q)}&limit=${q ? 20 : 6}${includeInactive ? '&includeInactive=true' : ''}`,
    );
    return { items: res.items, nextCursor: null };
  }, [includeInactive]);

  return (
    <EntityCombobox<Industry>
      label={label}
      required={required}
      error={error}
      info={info}
      disabled={disabled}
      value={value}
      onChange={onChange}
      fetchOptions={fetchOptions}
      getKey={(i) => i.id}
      minChars={1}
      placeholder="Choose an industry"
      searchPlaceholder="Search industries…"
      emptyMessage="No industry matches. Add it under Clinical masters → Industries."
      // One line per option. The description was a full sentence and wrapped,
      // turning a six-item list into a scrolling block; the code is what
      // distinguishes two similarly named industries, so that is what stays.
      renderOption={(i) => (
        <span className="flex items-baseline justify-between gap-3">
          <span className="truncate text-neutral-900">{i.name}</span>
          <span className="shrink-0 font-mono text-caption font-normal text-neutral-500">{i.code}</span>
        </span>
      )}
      renderValue={(i) => i.name}
    />
  );
}

/** A picker value from a company row that carries the catalogue link. */
export function industryFromRef(
  ref: { id: string; code: string; name: string } | null | undefined,
): Industry | null {
  if (!ref) return null;
  return {
    id: ref.id, code: ref.code, name: ref.name, description: null,
    sortOrder: 0, isActive: true, companyCount: 0, packageCount: 0, hazardCount: 0,
  };
}
